import { Typography, Autocomplete, TextField, Box, Button } from "@mui/material";
import { useState, useEffect, useMemo } from 'react';
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "../../../../../../redux/store";
import { getFacilitiesAndUsersList } from "../../../../../../redux/slices/tickets";
import Iconify from "../../../../../../reusable-components/iconify";
import { filterFacCatType, getFullCategoryList, getFullFacilityList, getFullTypeList } from "../TicketHelper";
import LoadingAnimation from "../../../../../../reusable-components/loader/LoadingAnimation";

FacilityAndTicketTypeSelection.propTypes = {
  setIsAutoTicketMessagesEditorOpen: PropTypes.func,
  setTicketTypeFieldGroups: PropTypes.func,
  selectedFacility: PropTypes.object,
  setSelectedFacility: PropTypes.func,
  selectedTicketCategory: PropTypes.object,
  setSelectedTicketCategory: PropTypes.func,
  setSelectedTicketType: PropTypes.func,
  selectedTicketType: PropTypes.object,
  filteredCategories: PropTypes.array,
  setFilteredCategories: PropTypes.func,
  setFilteredTypes: PropTypes.func,
  filteredTypes: PropTypes.array,
  setConfirmedCategoryFacilityAndType: PropTypes.func,
}

export default function FacilityAndTicketTypeSelection({ setConfirmedCategoryFacilityAndType, setTicketTypeFieldGroups, selectedFacility, setSelectedFacility, selectedTicketCategory,
  setSelectedTicketCategory, selectedTicketType, setSelectedTicketType, filteredCategories, setFilteredCategories, filteredTypes, setFilteredTypes,
}) {
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const { facilities, ticketTypes, ticketCategories, facilityCategories, defaultFacilityId, facilityTypes } = useSelector((state) => state.tickets.data);
  const [isFacilitySelectOpen, setIsFacilitySelectOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (!facilities.length || !ticketTypes.length) {
        await dispatch(getFacilitiesAndUsersList());
      }
      setLoading(false);
      setTicketTypeFieldGroups([]);
    };
    fetchData();
  }, [])

  const [userClearedFacility, setUserClearedFacility] = useState(false);

  const ticketCategoriesFullList = useMemo(() => getFullCategoryList(ticketCategories, facilityCategories, user), [ticketCategories, facilityCategories, user]);
  const facilitiesFullList = useMemo(() => getFullFacilityList(facilities), [facilities]);
  const ticketTypesFullList = useMemo(() => getFullTypeList(ticketTypes, facilityTypes, user), [ticketTypes, facilityTypes, user]);

  useEffect(() => {
    if (!loading) filterFacCatType(selectedFacility, facilitiesFullList, facilityCategories, defaultFacilityId ?? 0, facilityTypes, false, setFilteredCategories, setFilteredTypes, ticketCategoriesFullList,
      ticketTypesFullList, selectedTicketCategory, selectedTicketType, setSelectedFacility, setSelectedTicketCategory, setSelectedTicketType, setConfirmedCategoryFacilityAndType, userClearedFacility);
  }, [facilitiesFullList, selectedFacility, loading]);

  const categoryFacilityAndTypeSet = !loading && (selectedFacility || facilities.length === 0) && (selectedTicketType || filteredTypes.length === 0) &&
    (selectedTicketCategory || filteredCategories.length === 0);

  return loading ? <LoadingAnimation /> : <Box sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
    <Typography sx={{mt: 2}}>Select a Facility</Typography>
    {facilitiesFullList.length > 0 && (
      <Autocomplete
        fullWidth
        size="small"
        options={facilitiesFullList}
        value={selectedFacility}
        open={isFacilitySelectOpen}
        onOpen={() => setIsFacilitySelectOpen(true)}
        onClose={() => setIsFacilitySelectOpen(false)}
        getOptionLabel={(option) => option.facilityName}
        renderInput={(params) => <TextField {...params} placeholder="Facility Name" autoComplete="off"/>}
        onChange={(event, newValue) => {
          if(!newValue) {
            setUserClearedFacility(true);
            setSelectedFacility(null);
            setSelectedTicketCategory(null);
            setSelectedTicketType(null);
          }
          else if (userClearedFacility) setUserClearedFacility(false);
          setSelectedFacility(newValue)
        }}
      />
    )}
    {selectedFacility && filteredCategories?.length > 0 && 
      <Box sx={{my: 2}}>
        <Typography>Select a Ticket Category</Typography>
        <Autocomplete
          options={filteredCategories}
          getOptionLabel={(option) => option.label}
          value={selectedTicketCategory}
          size="small"
          onChange={(_, newValue) => {
            if(!newValue) setSelectedTicketType(null);
            setSelectedTicketCategory(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                inputMode: 'none',
                readOnly: true,
              }}
            />
          )}
        />
      </Box>
    }
    {
      selectedFacility && (!filteredCategories?.length > 0 || selectedTicketCategory) && filteredTypes?.length > 0 && 
      <Box sx={{mt: !filteredCategories?.length ? 2 : 0}}>
        <Typography>Select a Ticket Type</Typography>
        <Autocomplete
          options={filteredTypes}
          getOptionLabel={(option) => option.name || ''}
          value={selectedTicketType}
          size="small"
          onChange={(_, newValue) => setSelectedTicketType(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                inputMode: 'none',
                readOnly: true,
              }}
            />
          )}
        />
      </Box>
    }
    <Button
      variant="contained"
      disabled={!categoryFacilityAndTypeSet}
      sx={{mt: 'auto', alignSelf: 'flex-end', justifySelf: 'flex-end'}}
      onClick={() => setConfirmedCategoryFacilityAndType(true)}
    >
      Next <Iconify icon="mdi:chevron-right" />
    </Button>
  </Box>
}